import { useEffect, useRef, useState } from "react";
import { Loader2, Cloud, AlertTriangle } from "lucide-react";
import { csrfFetch } from "@lib/csrfFetch";
import { useUploadProcessingPoll } from "~/hooks/useUploadProcessingPoll";
import type { UploadRecord } from "~/lib/uploads";
import { VideoPlayer } from "./VideoPlayer";

// The datacenter-to-datacenter ingest path: the browser never carries the
// bytes. We hand the server a URL, it fetches straight into R2, and the same
// Upload row a browser upload would produce falls out the far end. The panel
// mirrors the queue widget's progress language, then reuses the player path
// once an upload_id lands.

// The RemoteFetchSession JSON the controller serializes. `id` is the session's
// uuid (addressed in the show/poll URL); `upload_id` is present only once the
// fetch completes and an Upload row exists.
interface RemoteFetchSession {
  id: string;
  source_url: string;
  provider: string | null;
  status: "pending" | "fetching" | "completed" | "failed" | string;
  bytes_fetched: number | null;
  total_bytes: number | null;
  error_message: string | null;
  upload_id: number | null;
}

// The session show endpoint is cheap and the fetch settles in seconds, so poll
// faster than the transcode poll — the founder wants to watch bytes climb.
const SESSION_POLL_INTERVAL_MS = 2000;

const TERMINAL_STATUSES = new Set(["completed", "failed"]);

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

// Render the fetched upload through the same VideoPlayer the browser-upload
// slot lands on. Seed the processing poll with the show payload so the player
// paints immediately and then upgrades preview → original → rungs live.
function FetchedUploadPlayer({ upload }: { upload: UploadRecord }) {
  const { upload: fresh } = useUploadProcessingPoll(upload.id, upload);
  const video = fresh?.video ?? upload.video ?? null;

  if (!video) return null;

  return (
    <div className="mt-4" data-testid="remote-fetch-player">
      <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-base-900">
        <VideoPlayer
          video={video}
          processingStatus={fresh?.processing_status ?? "processing"}
          filename={`upload-${upload.id}`}
          className="absolute inset-0 size-full rounded-none"
        />
      </div>
    </div>
  );
}

export function RemoteFetchPanel() {
  const [sourceUrl, setSourceUrl] = useState("");
  const [session, setSession] = useState<RemoteFetchSession | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The Upload row the completed session resolves to (fetched via the upload
  // show endpoint). Seeds the player and its processing poll.
  const [upload, setUpload] = useState<UploadRecord | null>(null);
  const cancelledRef = useRef(false);

  const status = session?.status ?? null;
  const isTerminal = status != null && TERMINAL_STATUSES.has(status);
  const uploadId = session?.upload_id ?? null;

  // Poll the session's show endpoint until it reaches a terminal status. The
  // fetch itself runs server-side in a worker, so this is pure progress
  // reflection — no bytes flow through the browser.
  useEffect(() => {
    if (!session || isTerminal) return;
    cancelledRef.current = false;

    const tick = async () => {
      try {
        const res = await csrfFetch(`/api/v1/remote_fetches/${session.id}`, { method: "GET" });
        if (cancelledRef.current || !res.ok) return;
        const fresh = (await res.json()) as RemoteFetchSession;
        if (!cancelledRef.current) setSession(fresh);
      } catch {
        // Transient failure — retry on the next interval.
      }
    };

    const handle = setInterval(tick, SESSION_POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(handle);
    };
  }, [session, isTerminal]);

  // Once the fetch completes and names an upload_id, pull the Upload row so the
  // player renders exactly as it does for a browser upload.
  useEffect(() => {
    if (status !== "completed" || uploadId == null) return;
    let cancelled = false;

    (async () => {
      const res = await csrfFetch(`/api/v1/uploads/${uploadId}`, { method: "GET" });
      if (cancelled || !res.ok) return;
      const record = (await res.json()) as UploadRecord;
      if (!cancelled) setUpload(record);
    })();

    return () => {
      cancelled = true;
    };
  }, [status, uploadId]);

  async function handleFetch() {
    const url = sourceUrl.trim();
    if (!url || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setSession(null);
    setUpload(null);

    try {
      const res = await csrfFetch("/api/v1/remote_fetches", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_url: url }),
      });

      if (res.status !== 201) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setSubmitError(body?.error ?? `Fetch failed to start (${res.status})`);
        return;
      }

      const created = (await res.json()) as RemoteFetchSession;
      setSession(created);
    } catch {
      setSubmitError("Could not reach the server.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-10 border-t border-neutral-200 pt-6" data-testid="remote-fetch-panel">
      <div className="mb-1 flex items-center gap-2">
        <Cloud className="size-4 text-primary-500" />
        <span className="text-sm font-medium text-base-600">Remote fetch (paste a URL)</span>
      </div>
      <p className="mb-3 text-xs text-neutral-400">
        Datacenter-to-datacenter ingest — the server fetches the URL straight into R2. The browser
        never uploads the bytes.
      </p>

      <div className="flex items-center gap-2">
        <input
          type="url"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleFetch();
          }}
          placeholder="https://example.com/video.mp4"
          data-testid="remote-fetch-url"
          className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm text-base-600"
        />
        <button
          type="button"
          onClick={handleFetch}
          disabled={submitting || sourceUrl.trim() === ""}
          data-testid="remote-fetch-submit"
          className="rounded-md bg-primary-500 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {submitting ? "Starting…" : "Fetch"}
        </button>
      </div>

      {submitError && (
        <p className="mt-2 flex items-center gap-1 text-xs text-error-500" data-testid="remote-fetch-error">
          <AlertTriangle className="size-3.5" />
          {submitError}
        </p>
      )}

      {session && (
        <div className="mt-4 rounded-lg border border-neutral-200 p-4" data-testid="remote-fetch-status">
          <div className="flex items-center gap-2">
            {!isTerminal && <Loader2 className="size-4 animate-spin text-primary-500" />}
            <span className="text-sm font-medium text-base-600" data-testid="remote-fetch-status-label">
              {session.status}
            </span>
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-neutral-500">
            <dt>Fetched</dt>
            <dd data-testid="remote-fetch-bytes">{formatBytes(session.bytes_fetched)}</dd>
            <dt>Total</dt>
            <dd data-testid="remote-fetch-total">{formatBytes(session.total_bytes)}</dd>
            {session.upload_id != null && (
              <>
                <dt>Upload id</dt>
                <dd data-testid="remote-fetch-upload-id">{session.upload_id}</dd>
              </>
            )}
          </dl>
          {session.status === "failed" && session.error_message && (
            <p className="mt-2 flex items-start gap-1 text-xs text-error-500" data-testid="remote-fetch-failed">
              <AlertTriangle className="mt-px size-3.5 shrink-0" />
              <span className="break-words">{session.error_message}</span>
            </p>
          )}
        </div>
      )}

      {upload && <FetchedUploadPlayer upload={upload} />}
    </div>
  );
}
